// src/components/common/ProtectedPdfViewer.jsx
// View-only PDF viewer. Pages are drawn onto <canvas> with PDF.js, so unlike the
// Google Docs viewer there is no toolbar, pop-out, download or print button, and
// the file URL never appears in the page markup.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiMinus, HiPlus, HiRefresh } from "react-icons/hi";

const ZOOM_STEPS = [1, 1.25, 1.5, 2];
const MAX_PIXEL_RATIO = 2; // caps canvas memory on high-DPI phones
const A4_RATIO = 297 / 210;

let pdfjsPromise;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/legacy/build/pdf").then((pdfjs) => {
      // Copied into public/ by scripts/copy-pdf-worker.mjs; ?v= busts caches on upgrades.
      pdfjs.GlobalWorkerOptions.workerSrc =
        `${process.env.PUBLIC_URL}/pdf.worker.min.js?v=${pdfjs.version}`;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

const blockEvent = (e) => e.preventDefault();

function PdfPage({ pdf, pageNumber, width, scrollRoot }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ratio, setRatio] = useState(A4_RATIO);

  // Only render pages that are near the visible area.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || visible) return;
    if (!("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); io.disconnect(); } },
      { root: scrollRoot, rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollRoot, visible]);

  useEffect(() => {
    if (!visible || !width) return;
    let cancelled = false;
    let renderTask;
    pdf.getPage(pageNumber)
      .then((page) => {
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
        const viewport = page.getViewport({ scale: (width / base.width) * pixelRatio });
        const canvas = canvasRef.current;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        setRatio(base.height / base.width);
        renderTask = page.render({ canvasContext: canvas.getContext("2d"), viewport });
        return renderTask.promise;
      })
      .catch((err) => {
        if (err?.name !== "RenderingCancelledException") console.error(err);
      });
    return () => { cancelled = true; renderTask?.cancel(); };
  }, [pdf, pageNumber, width, visible]);

  return (
    <div
      ref={wrapRef}
      className="bg-white shadow-sm mx-auto"
      style={{ width, height: Math.round(width * ratio) }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" aria-label={`Page ${pageNumber}`} />
    </div>
  );
}

export default function ProtectedPdfViewer({ url, title }) {
  const scrollRef = useRef(null);
  const [scrollRoot, setScrollRoot] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [progress, setProgress] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(0);
  const [attempt, setAttempt] = useState(0);

  const scrollCallbackRef = useCallback((node) => {
    scrollRef.current = node;
    setScrollRoot(node);
  }, []);

  // Load the document.
  useEffect(() => {
    let cancelled = false;
    let loadingTask;
    setStatus("loading");
    setProgress(0);
    setPdf(null);

    loadPdfjs()
      .then((pdfjs) => {
        if (cancelled) return;
        loadingTask = pdfjs.getDocument({
          url,
          disableRange: true,
          isEvalSupported: false, // hardening against malicious font payloads
        });
        loadingTask.onProgress = ({ loaded, total }) => {
          if (!cancelled && total) setProgress(Math.min(99, Math.round((loaded / total) * 100)));
        };
        return loadingTask.promise;
      })
      .then((loaded) => {
        if (!loaded || cancelled) return;
        setPdf(loaded);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("PDF load failed:", err);
        setStatus("error");
      });

    return () => {
      cancelled = true;
      loadingTask?.destroy(); // also destroys the loaded document
    };
  }, [url, attempt]);

  // Track the available width so pages fit the screen.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(Math.floor(el.clientWidth));
    measure();
    let timer;
    const onResize = () => { clearTimeout(timer); timer = setTimeout(measure, 150); };
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(timer); window.removeEventListener("resize", onResize); };
  }, [status]);

  // Block the save-page / print shortcuts while a paper is open.
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key?.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (key === "s" || key === "p")) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const zoom = ZOOM_STEPS[zoomIdx];
  const pagePadding = 16;
  const pageWidth = Math.max(0, Math.floor((containerWidth - pagePadding * 2) * zoom));

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col print:hidden select-none"
      style={{ height: "80vh", minHeight: 480, WebkitTouchCallout: "none" }}
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
      onCopy={blockEvent}
    >
      {/* Toolbar — deliberately no download / print / pop-out */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
        <span className="truncate font-medium">
          {status === "ready" ? `${pdf.numPages} page${pdf.numPages === 1 ? "" : "s"}` : title}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
            disabled={zoomIdx === 0 || status !== "ready"}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Zoom out"
          >
            <HiMinus />
          </button>
          <span className="w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            disabled={zoomIdx === ZOOM_STEPS.length - 1 || status !== "ready"}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Zoom in"
          >
            <HiPlus />
          </button>
        </div>
      </div>

      <div ref={scrollCallbackRef} className="flex-1 overflow-auto bg-gray-100" style={{ scrollbarGutter: "stable" }}>
        {status === "loading" && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-500 text-sm">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            Loading paper{progress ? `… ${progress}%` : "…"}
          </div>
        )}

        {status === "error" && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
            <p className="text-gray-600 font-semibold">Couldn't load this paper</p>
            <p className="text-gray-400 text-sm max-w-xs">
              Check your connection and try again, or use the WhatsApp button above to request it.
            </p>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors"
            >
              <HiRefresh /> Try again
            </button>
          </div>
        )}

        {status === "ready" && pageWidth > 0 && (
          <div className="flex flex-col gap-4" style={{ padding: pagePadding, width: pageWidth + pagePadding * 2 }}>
            {Array.from({ length: pdf.numPages }, (_, i) => (
              <PdfPage
                key={i + 1}
                pdf={pdf}
                pageNumber={i + 1}
                width={pageWidth}
                scrollRoot={scrollRoot}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
